import { Shield, FileText, AlertTriangle, CheckCircle, Mail } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const TermsOfService = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-white/50">Effective Date: March 2025</p>
        </div>

        <div className="space-y-6 text-white/70">
          {/* Platform Overview */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00F0FF]" />
              1. Platform Overview
            </h2>
            <p className="mb-3">
              MatchStake is a skill-based competition platform where users participate in competitive matches using virtual credits.
            </p>
            <p className="text-[#FFB800] font-semibold">
              MatchStake does NOT provide gambling or wagering services.
            </p>
          </section>

          {/* User Accounts */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">2. User Accounts</h2>
            <p className="mb-3">Users must:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Be at least 18 years old</li>
              <li>Provide accurate account information</li>
              <li>Maintain the security of their account</li>
            </ul>
            <p>MatchStake reserves the right to suspend or terminate accounts for violations of these terms.</p>
          </section>

          {/* Credits System */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">3. Credits System</h2>
            <p className="mb-3">Users may obtain platform credits by submitting payments through supported payment methods.</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Credits are a digital balance used for platform participation</li>
              <li>Credits have no direct cash value outside the platform</li>
              <li>Credits are not a form of currency or stored monetary value</li>
            </ul>
            <p className="font-medium text-white">Credits are used for:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Competition access</li>
              <li>Platform features</li>
            </ul>
          </section>

          {/* Deposits */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">4. Deposits</h2>
            <p className="mb-3">Users may submit payments to receive credits.</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Payments are converted into credits after verification</li>
              <li>Users must provide valid payment confirmation</li>
              <li>Failure to provide proof may result in denial of credits</li>
            </ul>
            <p className="text-[#FFB800]">MatchStake is not responsible for incorrectly submitted payments.</p>
          </section>

          {/* Competition Access */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">5. Competition Access</h2>
            <p className="mb-3">Users use credits to enter skill-based competitions.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Entry into a competition is a participation fee for access</li>
              <li className="text-[#00F0FF] font-medium">Entry payments do NOT contribute to prize pools</li>
              <li>Entry payments are retained by MatchStake as platform revenue</li>
            </ul>
          </section>

          {/* Rewards */}
          <section className="glass-card rounded-xl p-6 border border-[#00FF94]/30">
            <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#00FF94]" />
              6. Rewards
            </h2>
            <p className="mb-3 font-medium text-white">All rewards on MatchStake are:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li className="text-[#00FF94]">Funded and distributed by MatchStake</li>
              <li>Predefined and not dynamically generated from user payments</li>
            </ul>
            <p className="mb-3">Rewards are issued after:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Match completion</li>
              <li>Result verification (AI and/or admin review)</li>
            </ul>
            <p className="text-white/50 text-sm">MatchStake reserves the right to adjust reward structures at any time.</p>
          </section>

          {/* No Wagering */}
          <section className="glass-card rounded-xl p-6 border border-[#FF2E2E]/30 bg-[#FF2E2E]/5">
            <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF2E2E]" />
              7. No Wagering or Gambling
            </h2>
            <p className="mb-3 font-medium text-white">MatchStake does NOT facilitate:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3 text-[#FF2E2E]">
              <li>Gambling</li>
              <li>Betting</li>
              <li>Wagering</li>
            </ul>
            <p className="font-semibold">Users do not compete for pooled funds contributed by other users.</p>
          </section>

          {/* Withdrawals */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">8. Withdrawals</h2>
            <p className="mb-3">Users may request withdrawals of available balances.</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>All withdrawals are subject to review and approval</li>
              <li>MatchStake may request additional verification</li>
              <li>Processing times may vary</li>
            </ul>
            <p className="mb-2">MatchStake reserves the right to deny withdrawals in cases of:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Fraud</li>
              <li>Abuse</li>
              <li>Terms violations</li>
            </ul>
          </section>

          {/* Fraud and Abuse */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">9. Fraud and Abuse</h2>
            <p className="mb-3">The following activities are strictly prohibited:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3 text-[#FF2E2E]">
              <li>Fake payment submissions</li>
              <li>Chargebacks or payment disputes</li>
              <li>Exploiting platform mechanics</li>
              <li>Collusion or match manipulation</li>
            </ul>
            <p className="mb-2">Violations may result in:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Account suspension</li>
              <li>Loss of credits</li>
              <li>Permanent ban</li>
            </ul>
          </section>

          {/* Platform Control */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">10. Platform Control</h2>
            <p className="mb-2">MatchStake reserves the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Modify or discontinue features</li>
              <li>Adjust credit values or rewards</li>
              <li>Suspend accounts for violations</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3">11. Limitation of Liability</h2>
            <p className="mb-2">MatchStake is not liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Loss of funds due to user error</li>
              <li>Payment issues from third-party providers</li>
              <li>Service interruptions</li>
            </ul>
          </section>

          {/* Acceptance */}
          <section className="glass-card rounded-xl p-6 border border-[#7000FF]/30">
            <h2 className="font-semibold text-white mb-3">12. Acceptance of Terms</h2>
            <p className="font-medium">By using MatchStake, you agree to these Terms of Service.</p>
          </section>

          {/* Contact */}
          <section className="glass-card rounded-xl p-6">
            <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#00F0FF]" />
              13. Contact
            </h2>
            <p>For support, contact:</p>
            <a href="mailto:matchstakesupport@gmail.com" className="text-[#00F0FF] hover:underline">
              matchstakesupport@gmail.com
            </a>
          </section>

          {/* Compliance Statement */}
          <section className="p-6 rounded-xl bg-gradient-to-r from-[#00F0FF]/10 to-[#7000FF]/10 border border-[#00F0FF]/30">
            <p className="text-center text-white font-medium">
              "All rewards are funded and distributed by MatchStake. User payments are for competition access and do not contribute to prize pools."
            </p>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TermsOfService;
